/*
 * PDP gallery — Pause Rituals
 * -----------------------------------------------------------------------------
 * Two jobs that sit on top of media-gallery.js. Neither changes the layout.
 *
 * 1. WARM THE IMAGES so a thumbnail click swaps instantly.
 *    Every gallery image after the first is rendered with loading="lazy", and above
 *    750px the layout hides all but the active slide with `display: none`
 *    (.product--thumbnail .product__media-item:not(.is-active)). A lazy image inside
 *    a display:none box is never fetched — the browser only starts downloading it at
 *    the moment the box becomes visible. So the class swap WAS instant; the picture
 *    just wasn't there yet. Flipping those images to eager once the page has settled
 *    puts them in cache before the click, without touching LCP.
 *
 * 2. MAKE NAVIGATION WORK AT EVERY WIDTH, from every input.
 *    Above 750px the gallery had none at all: every inactive slide is display:none,
 *    so the carousel cannot be swiped, SliderComponent.initPages() bails out
 *    (fewer than two slides have width) and Dawn's arrows are hidden by
 *    `.slider--mobile + .slider-buttons { display: none }`. Below 750px the carousel
 *    scrolls natively, but the thumbnail ring depended on SliderComponent's
 *    slideChanged, which goes permanently silent whenever its measurement pass runs
 *    while the slides have no width.
 *
 *    Four inputs — arrows, arrow keys, horizontal trackpad swipe, finger swipe — all
 *    routed through the same setActiveMedia() the thumbnails use, plus a sync that
 *    reads the carousel's scroll position directly. The main image, the `.is-active`
 *    slide and the thumbnail ring therefore cannot disagree, whichever one moved.
 */
(function () {
  'use strict';

  var WHEEL_COOLDOWN = 450; // ms between trackpad-swipe steps
  var WHEEL_THRESHOLD = 8; // ignore incidental horizontal drift
  var SWIPE_MIN_DISTANCE = 40; // px of finger travel before a swipe counts

  /* ---- 1. Image warming --------------------------------------------------- */

  function warmMedia(gallery) {
    if (gallery.dataset.mediaWarmed === '1') return;
    gallery.dataset.mediaWarmed = '1';

    gallery.querySelectorAll('.product__media-list img[loading="lazy"]').forEach(function (img) {
      // Switching loading from lazy to eager on a pending image starts the fetch
      // immediately, even while the image is inside a display:none container.
      img.loading = 'eager';
      // Low priority so warming never competes with the visible image or the fonts.
      if ('fetchPriority' in img) img.fetchPriority = 'low';
    });
  }

  function scheduleWarm(gallery) {
    if (gallery.dataset.mediaWarmScheduled === '1') return;
    gallery.dataset.mediaWarmScheduled = '1';

    var warm = function () {
      warmMedia(gallery);
    };

    // Whichever comes first: the page finishing load, or the visitor showing intent
    // by moving onto the gallery. Intent usually wins, which is the point.
    if (document.readyState === 'complete') window.setTimeout(warm, 0);
    else window.addEventListener('load', warm, { once: true });

    gallery.addEventListener('pointerenter', warm, { once: true });
    gallery.addEventListener('touchstart', warm, { once: true, passive: true });
    gallery.addEventListener('focusin', warm, { once: true });
  }

  /* ---- 2. Navigation ------------------------------------------------------ */

  /*
    DIRECT CHILDREN ONLY — this must never be a descendant selector.
    Each slide <li> carries data-media-id="{section}-{media}", but
    snippets/product-thumbnail.liquid ALSO stamps a bare data-media-id="{media}" on the
    inner .product__media / deferred-media / product-model node. A descendant selector
    collected both, so stepping could land on an inner node; setActiveMedia() then
    marked THAT active and no <li> was left with .is-active — which the desktop layout
    (`.product--thumbnail .product__media-item:not(.is-active) { display: none }`)
    renders as a completely empty gallery.
  */
  function slides(gallery) {
    var list = gallery.querySelector('.product__media-list');
    if (!list) return [];
    return Array.prototype.filter.call(list.children, function (el) {
      return el.hasAttribute('data-media-id');
    });
  }

  function activeIndex(gallery, items) {
    var index = -1;
    for (var i = 0; i < items.length; i++) {
      if (items[i].classList.contains('is-active')) {
        index = i;
        break;
      }
    }
    return index < 0 ? 0 : index;
  }

  // Wraps at both ends — with a handful of product images that reads better than
  // arrows that grey out, and it keeps the two buttons symmetrical.
  function step(gallery, delta) {
    if (typeof gallery.setActiveMedia !== 'function') return;

    var items = slides(gallery);
    if (items.length < 2) return;

    var next = (activeIndex(gallery, items) + delta + items.length) % items.length;
    gallery.setActiveMedia(items[next].dataset.mediaId, false);
  }

  function setupNavigation(gallery) {
    if (gallery.dataset.galleryNavReady === '1') return;
    gallery.dataset.galleryNavReady = '1';

    gallery.querySelectorAll('[data-pdp-gallery-prev]').forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        step(gallery, -1);
      });
    });

    gallery.querySelectorAll('[data-pdp-gallery-next]').forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        step(gallery, 1);
      });
    });

    /*
      Arrow keys, whenever focus is anywhere in the gallery — which after clicking an
      arrow or tabbing to a thumbnail it is. Not gated to desktop: on mobile this
      simply scrolls the carousel to the same slide, which is the right outcome.
    */
    gallery.addEventListener('keydown', function (event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      var target = event.target;
      if (target && target.matches && target.matches('input, textarea, select')) return;

      event.preventDefault();
      step(gallery, event.key === 'ArrowRight' ? 1 : -1);
    });

    var viewer = gallery.querySelector('.product__media-list');
    if (!viewer) return;

    /*
      Horizontal trackpad swipe, at EVERY width.
      On desktop there is nothing to scroll, so this is the only way the gesture can
      do anything. Below 750px the carousel does scroll natively, but leaving it to
      the browser gave a free-running scroll that often landed between two images and
      did not reliably settle — driving it a whole slide at a time is predictable in
      both directions, and it is what "one swipe, one image" should feel like.
      preventDefault also stops the browser reading the gesture as a back/forward
      navigation, the usual way to lose your place on a product page.
      smooth-scroll.js ignores horizontal-dominant wheel events, so the two never
      contend for the same gesture.
    */
    var lastStep = 0;
    viewer.addEventListener(
      'wheel',
      function (event) {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        if (Math.abs(event.deltaX) < WHEEL_THRESHOLD) return;

        event.preventDefault();

        var now = Date.now();
        if (now - lastStep < WHEEL_COOLDOWN) return;
        lastStep = now;

        step(gallery, event.deltaX > 0 ? 1 : -1);
      },
      { passive: false }
    );

    setupScrollSync(gallery, viewer);
    setupTouchFallback(gallery, viewer);
  }

  /* ---- 3. Mobile: scroll position is the source of truth ------------------ */

  /*
    Derives the active slide straight from the carousel's scroll position instead of
    going through SliderComponent's `slideChanged`.

    That path only works when initPages() has measured a valid slide offset, and it
    silently gives up whenever the measurement ran while the slides had no width —
    a section reload in the theme editor, a restored background tab, or a resize
    across the 750px boundary (above it every inactive slide is display:none, so
    fewer than two slides have width and initPages() returns early, leaving
    sliderItemOffset undefined and currentPage NaN for good).

    Reading scrollLeft has none of those failure modes.
  */
  function setupScrollSync(gallery, viewer) {
    var frame = null;

    viewer.addEventListener(
      'scroll',
      function () {
        if (frame) return;
        frame = window.requestAnimationFrame(function () {
          frame = null;
          syncFromScroll(gallery, viewer);
        });
      },
      { passive: true }
    );
  }

  function syncFromScroll(gallery, viewer) {
    // Our own animation: media-gallery.js sets this while it scrolls to a slide, so
    // the ones it passes over on the way do not each flash as active.
    if (gallery.programmaticScroll) return;

    var items = slides(gallery);
    if (items.length < 2) return;

    var nearest = null;
    var shortest = Infinity;
    for (var i = 0; i < items.length; i++) {
      var distance = Math.abs(items[i].offsetLeft - viewer.scrollLeft);
      if (distance < shortest) {
        shortest = distance;
        nearest = items[i];
      }
    }

    if (!nearest || nearest.classList.contains('is-active')) return;
    if (typeof gallery.setActiveMedia !== 'function') return;

    // scrollViewer: false — the carousel is already where the finger left it.
    gallery.setActiveMedia(nearest.dataset.mediaId, false, { scrollViewer: false });
  }

  /* ---- 4. Touch fallback -------------------------------------------------- */

  /*
    A finger swipe should move the carousel natively. If it did, this does nothing.
    If it did not — a stray touch-action or overflow rule anywhere up the tree is
    enough to suppress it — this turns the gesture into a step instead.

    The scrollLeft comparison is what makes it safe: it only fires when the native
    scroll produced no movement at all, so it can never advance the gallery twice
    for one swipe.
  */
  function setupTouchFallback(gallery, viewer) {
    var startX = 0;
    var startY = 0;
    var startScroll = 0;
    var tracking = false;

    viewer.addEventListener(
      'touchstart',
      function (event) {
        if (event.touches.length !== 1) {
          tracking = false;
          return;
        }
        tracking = true;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
        startScroll = viewer.scrollLeft;
      },
      { passive: true }
    );

    viewer.addEventListener(
      'touchend',
      function (event) {
        if (!tracking) return;
        tracking = false;

        var touch = event.changedTouches[0];
        if (!touch) return;

        var dx = touch.clientX - startX;
        var dy = touch.clientY - startY;

        if (Math.abs(dx) < SWIPE_MIN_DISTANCE) return;
        if (Math.abs(dx) <= Math.abs(dy)) return; // a vertical page scroll
        if (Math.abs(viewer.scrollLeft - startScroll) > 4) return; // native handled it

        step(gallery, dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  /* ---- Boot --------------------------------------------------------------- */

  function init(scope) {
    (scope || document).querySelectorAll('media-gallery.pdp-gallery').forEach(function (gallery) {
      scheduleWarm(gallery);
      setupNavigation(gallery);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
